import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQueryBuilder, useMutationBuilder } from "../useBuilders";
import { EXPERIENCES, STORYBOARDS, FETCH } from "../queryKeys";
import { experiencesStoryboardsApi } from "src/services/experiences/storyboards";
import {
  FetchStoryboardsResponse,
  StoryboardItem,
} from "src/types/experiences/storyboards";

// Shape of the cached infinite-query data
type StoryboardsInfiniteData = {
  pages: FetchStoryboardsResponse[];
  pageParams: unknown[];
};

export const useExperiencesStoryboards = (
  workspaceId: string,
  listId: string | null = null,
  query: string = "",
  type: string | null = null,
  status: string | null = null,
) =>
  useInfiniteQueryBuilder(
    [EXPERIENCES, STORYBOARDS, FETCH, workspaceId, listId ?? "", query, type ?? "", status ?? ""],
    ({ pageParam = 0 }) =>
      experiencesStoryboardsApi.getStoryboards({
        list: listId ?? undefined,
        type: type ?? undefined,
        status: status ?? undefined,
        page: pageParam,
        limit: 20,
        sort: "createdDate",
        order: "desc",
        query,
      }),
    0,
    (lastPage) =>
      lastPage.currentPage + 1 < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    undefined,
    !!workspaceId
  );

// Invalidate the workspace-level prefix so all filter/folder variants refresh
const storyboardsInvalidationKey = (workspaceId: string) => [
  EXPERIENCES, STORYBOARDS, FETCH, workspaceId,
];

// Map every cached infinite-query variant for this workspace through `updater`.
// This mutates the cache synchronously, so the list re-renders the moment the
// dialog closes — instead of waiting for the background refetch to round-trip.
const updateCachedStoryboards = (
  pages: FetchStoryboardsResponse[],
  updater: (records: StoryboardItem[]) => StoryboardItem[]
): FetchStoryboardsResponse[] =>
  pages.map((page) => ({ ...page, records: updater(page.records) }));

// Merge a partial update into the matching card across every cached list
// variant (all workspaces / filters / folders). Lets edits made elsewhere —
// e.g. a rename on the storyboard-settings page — show in the list cache
// immediately, without waiting for the list query to refetch.
//
// The edited card is also moved to the front of the first page: the server
// surfaces freshly-modified storyboards to the top, so doing it optimistically
// makes that reorder happen instantly on save instead of jumping later when the
// background refetch lands.
export const patchStoryboardInListCache = (
  queryClient: QueryClient,
  patch: Partial<StoryboardItem> & { id: string }
) => {
  queryClient.setQueriesData<StoryboardsInfiniteData>(
    { queryKey: [EXPERIENCES, STORYBOARDS, FETCH] },
    (data) => {
      if (!data) return data;

      // Find the existing record (it may live on any page) and merge the patch.
      const existing = data.pages
        .flatMap((page) => page.records)
        .find((record) => record.id === patch.id);
      if (!existing) return data;

      const patched = { ...existing, ...patch };

      // Drop it from wherever it currently is, then prepend to page 0.
      const withoutRecord = updateCachedStoryboards(data.pages, (records) =>
        records.filter((record) => record.id !== patch.id)
      );

      return {
        ...data,
        pages: withoutRecord.map((page, index) =>
          index === 0
            ? { ...page, records: [patched, ...page.records] }
            : page
        ),
      };
    }
  );
};

export const useCloneExperiencesStoryboard = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [EXPERIENCES, STORYBOARDS, "CLONE", workspaceId],
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      experiencesStoryboardsApi.cloneStoryboard(id, name),
    onSuccess: (clonedStoryboard) => {
      // Optimistically prepend the clone (lists are sorted createdDate desc).
      queryClient.setQueriesData<StoryboardsInfiniteData>(
        { queryKey: storyboardsInvalidationKey(workspaceId) },
        (data) =>
          data
            ? {
                ...data,
                pages: data.pages.map((page, index) =>
                  index === 0
                    ? { ...page, records: [clonedStoryboard, ...page.records] }
                    : page
                ),
              }
            : data
      );
      queryClient.invalidateQueries({
        queryKey: storyboardsInvalidationKey(workspaceId),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: storyboardsInvalidationKey(workspaceId),
      });
    },
  });
};

export const useRenameExperiencesStoryboard = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [EXPERIENCES, STORYBOARDS, "RENAME", workspaceId],
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      experiencesStoryboardsApi.renameStoryboard(id, name),
    onSuccess: (updated) => {
      // Reflect the new name in the list cache straight away (no refetch wait).
      patchStoryboardInListCache(queryClient, {
        id: updated.id,
        name: updated.name,
        modifiedDate: updated.modifiedDate,
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: storyboardsInvalidationKey(workspaceId),
      });
    },
  });
};

export const useDeleteExperiencesStoryboard = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [EXPERIENCES, STORYBOARDS, "DELETE", workspaceId],
    mutationFn: (id: string) => experiencesStoryboardsApi.deleteStoryboard(id),
    onSuccess: (_data, id) => {
      // Optimistically drop the deleted card from every cached list variant.
      queryClient.setQueriesData<StoryboardsInfiniteData>(
        { queryKey: storyboardsInvalidationKey(workspaceId) },
        (data) =>
          data
            ? {
                ...data,
                pages: updateCachedStoryboards(data.pages, (records) =>
                  records.filter((record) => record.id !== id)
                ),
              }
            : data
      );
      queryClient.invalidateQueries({
        queryKey: storyboardsInvalidationKey(workspaceId),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: storyboardsInvalidationKey(workspaceId),
      });
    },
  });
};

export const useAddStoryboardToList = (workspaceId: string) =>
  useMutationBuilder<void, { listId: string; storyboardId: string }>(
    [EXPERIENCES, STORYBOARDS, "ADD_TO_LIST", workspaceId],
    ({ listId, storyboardId }) =>
      experiencesStoryboardsApi.addStoryboardToList(listId, [storyboardId]),
    storyboardsInvalidationKey(workspaceId)
  );
